<template>
    <div>
        <div class="search-wrapper input-wrapper">
            <input
                :id="name"
                v-model="query"
                type="text"
                :class="['text-input', query && 'filled', valid || 'error']"
                @blur="
                    () => {
                        resetValue()
                        triggerValidate()
                    }
                "
                ref="inputField"
            />

            <FloatingLabel :label="displayName" :icon="icon" />

            <ul class="search-results" v-if="searchResults.length">
                <li
                    v-for="(result, index) in searchResults"
                    :key="index"
                    @click="selectResult(result.item.id, result.item.ref, true)"
                >
                    {{ result.item.title }}
                </li>
            </ul>
        </div>

        <div class="input-wrapper">
            <Field type="hidden" :name="name" v-model="value" />
            <div class="error" v-if="displayErrorMessage">{{ displayErrorMessage }}</div>
        </div>
    </div>
</template>

<script>
import Fuse from 'fuse.js'
import { useStore } from 'vuex'
import { useField, Field } from 'vee-validate'
import { titleCase } from '@js/utilities/language.js'
import ApiRequest from '@js/utilities/apiRequest.js'

// lodash components
import debounce from 'lodash/debounce'
import filter from 'lodash/filter'
import find from 'lodash/find'
import head from 'lodash/head'
import isFunction from 'lodash/isFunction'
import reject from 'lodash/reject'
import slice from 'lodash/slice'
import sortBy from 'lodash/sortBy'
import toLower from 'lodash/toLower'
import map from 'lodash/map'

export default {
    components: {
        Field,
    },
    props: {
        name: {
            type: String,
            required: true,
        },
        preSelectedValue: {
            type: Object,
        },
        noResultsErrorMessage: {
            type: String,
            required: false,
            default: 'No results found',
        },
        defaultOption: {
            type: Array,
            required: false,
        },
        appendDefaultOption: {
            type: Boolean,
            default: false,
        },
        apiResultHandler: Function,
        errorMessage: String,
        customErrorMessage: String,
        icon: String,
        label: String,
        limit: Number,
        options: Array,
        optionsUrl: String,
        validationRules: Object,
        vuexKey: String,
    },
    data() {
        return {
            clicked: false,
            defaultResult: null,
            list: [],
            noResults: false,
            query: null,
            searchResults: [],
            valid: true,
        }
    },
    setup(props) {
        const store = useStore()
        const { errorMessage, resetField, validate } = useField(props.name, props.validationRules, {
            label: titleCase(props.name),
        })

        return {
            errorMessage,
            resetField,
            validate,
            store,
        }
    },
    created() {
        this.emitter.on('selectResult', payload => {
            this.selectResult(payload.id, payload.ref)
        })
    },
    async mounted() {
        // Set the list options
        if (this.options) {
            this.list = this.options
        }

        // Set list options from URL
        if (this.optionsUrl) {
            const responseData = await ApiRequest.get(this.$store, this.optionsUrl)
            if (responseData) {
                let list = responseData

                if (isFunction(this.apiResultHandler)) {
                    list = this.apiResultHandler(list)
                }

                this.list = [...this.list, ...list]

                this.list = map(this.list, item => {
                    return {
                        id: item.id,
                        title: item.title,
                        ref: item.id + item.title,
                    }
                })

                if (this.defaultOption) {
                    this.defaultResult = head(filter(this.list, this.defaultOption))
                }
            }
        }

        if (this.preSelectedValue && !this.value) {
            this.$nextTick(() => {
                this.selectResult(this.preSelectedValue.id, this.preSelectedValue.ref)
            })
        }

        // If a vuex store value is present, set it
        if (this.value && this.list) {
            this.$nextTick(() => {
                if (this.isNumeric(this.value)) {
                    const option = this.list[this.value] ?? null

                    if (option) {
                        this.selectResult(option.id, option.ref)
                    }
                } else {
                    this.query = this.getTitle(null, this.value)
                }
            })
        }
    },
    watch: {
        query() {
            // Prevent query when a result is selected
            if (this.clicked) {
                this.clicked = false
                return this.clicked
            }

            // If there is no query, clear the result
            if (!this.query) {
                return this.selectResult(null, null)
            }

            this.noResults = false

            const fuse = new Fuse(this.list, {
                threshold: 0.1,
                distance: 5,
                keys: ['title'],
            })

            const searchResult = this.query ? sortBy(fuse.search(this.query), ['item'], ['desc']) : []

            // Show search results for selection
            this.searchResults = slice(searchResult, 0, this.limit || 10)

            const noResults = this.query && !this.searchResults.length

            // Show no result error if there is no default
            if (!this.defaultResult && noResults) {
                this.noResults = true
            }

            // Show a default if no result
            if (this.defaultResult && noResults) {
                this.searchResults = [
                    {
                        item: this.defaultResult,
                    },
                ]
            } else if (this.defaultResult && this.appendDefaultOption) {
                // Append the default option to all results
                this.searchResults = reject(this.searchResults, ['item.ref', this.defaultResult.ref])

                this.searchResults.push({
                    item: this.defaultResult,
                })
            }
        },
        searchResults() {
            const first = head(this.searchResults)

            if (first && first.item && toLower(first.item.title) === toLower(this.query)) {
                this.selectResult(first.item.id, first.item.ref)
            }
        },
    },
    computed: {
        value: {
            get() {
                this.resetField({
                    value: this.value,
                })

                return this.store.getters.fieldValue(this.name, this.vuexKey || 'data', !!this.vuexKey)
            },
        },
        internal: function () {
            return this.store.state.apply.internal[this.name]
        },
        displayName: function () {
            return this.label || this.titleCase(this.name)
        },
        serverError() {
            return this.store.getters.fieldServerError(this.name)
        },
        displayErrorMessage() {
            return this.errorMessage || this.noResults
                ? (this.noResults && this.noResultsErrorMessage) || this.customErrorMessage || this.errorMessage
                : this.serverError
        },
    },
    methods: {
        selectResult: function (id, ref, userInvoked = false) {
            this.clicked = true

            if (this.vuexKey) {
                this.store.commit('setDataFieldValue', {
                    name: this.name,
                    value: id,
                    key: this.vuexKey,
                })
            } else {
                this.store.dispatch('setFieldValue', {
                    name: this.name,
                    value: id,
                })
            }

            this.store.commit('setInternal', {
                name: this.name,
                value: ref,
            })

            this.query = ref ? this.getTitle(ref) : null
            this.searchResults = []

            if (id && userInvoked) {
                this.emitter.emit('autocompleteField:selected', {
                    name: this.name,
                    value: id,
                })
            }

            this.triggerValidate()
        },
        triggerValidate: function () {
            this.$nextTick(async () => {
                this.valid = await this.validate()
            })
        },
        resetValue: debounce(function () {
            if (this.searchResults.length || (this.searchResults.length === 0 && this.query === '')) {
                this.selectResult(null, null)
            }
        }, 250),
        getTitle: function (ref = null, id = null) {
            const item = find(this.list, item => {
                return item.ref == (ref || this.internal) || item.id == (id || this.internal)
            })

            if (item && item.title) {
                this.resetField({
                    value: this.value,
                })
                return item.title
            }
            return null
        },
        focus() {
            this.autoFocus(this.$refs.inputField)
        },
        isNumeric(str) {
            if (typeof number == 'number') {
                return true
            }

            if (typeof str != 'string') {
                return false
            }

            return (
                !isNaN(str) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
                !isNaN(parseFloat(str))
            ) // ...and ensure strings of whitespace fail
        },
    },
}
</script>

<style lang="scss" scoped>
@import '~scss/mixins/rem';

.search-wrapper {
    .search-results {
        background: #ffffff;
        border-radius: 4px;
        border: 1px solid #e0e1e5;
        bottom: 0;
        position: absolute;
        transform: translate(0, 100%);
        width: 100%;
        z-index: 10000;

        li {
            background: #ffffff;
            font-size: rem(16px);
            margin-bottom: 1px;
            padding: 15px 10px;
            position: relative;
            text-align: left;

            &:after {
                border-bottom: 1px solid #e0e1e5;
                bottom: -1px;
                content: ' ';
                left: 10px;
                position: absolute;
                width: calc(100% - 20px);
            }

            &:last-of-type {
                margin-bottom: 0;

                &:after {
                    display: none;
                }
            }

            &:hover {
                background: #e0e1e5;
                cursor: pointer;
            }
        }
    }
}

.input-container {
    .address-field {
        margin-bottom: 20px;
    }
}
</style>
